import React, { useEffect, useMemo, useState } from "react";
import styles from "./savior-detail.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import StarContainer from "../../components/star-container/star-container";
import Header from "../../components/header/header";
import ScrollUpButton from "../../components/buttons/scroll-up-button/scroll-up-button";
import Footer from "../../components/footer/footer";
import Portrait from "../../components/savior/portrait/portrait";
import StatusTab from "../../components/savior/modal/modal-right/status-tab";
import SkillTab from "../../components/savior/modal/modal-right/skill-tab";
import ProfileDescTab from "../../components/savior/modal/modal-right/profile-desc-tab";
import FullScreenModal from "../../components/savior/modal/modal-left/full-screen-modal";
import { active as activateFullScreen, inactive as deactivateFullScreen } from "../../store/reducers/toggle/fullScreenArcpintToggle";
import { selectSaviorById } from "../../store/selectors/saviorSelectors";
import { getUIText } from "../../translation";
import { LOCALES, SAVIOR_ATK_TYPE, SAVIOR_ATTRIBUTE, SAVIOR_CLASS } from "../../constants";

export default function SaviorDetail() {
    const theme = useSelector((state) => state.theme.value);
    const lang = useSelector((state) => state.lang.value);
    const dispatch = useDispatch();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const savior = useSelector((state) => selectSaviorById(state, Number(id)));

    const canBlossom = !!(savior && savior.rank === "SR" && savior.blossom);

    const [isBlossomed, setIsBlossomed] = useState(false);

    // ?b=true 로 직접 접근한 경우: 원래 SR인 캐릭터만 개화 상태로 시작(SSR이면 무시), 나머지는 기본값
    useEffect(() => {
        if (!savior || savior.rank !== "SR" || !savior.blossom) {
            setIsBlossomed(false);
            return;
        }
        setIsBlossomed(searchParams.get("b") === "true");
    }, [savior]);

    const toggleBlossom = () => {
        const next = !isBlossomed;
        setIsBlossomed(next);
        setSearchParams(next ? { b: "true" } : {}, { replace: true });
    };

    const displaySavior = useMemo(() => {
        if (!savior) return savior;
        if (isBlossomed && savior.blossom) {
            return {
                ...savior,
                rank: "SSR",
                base_stat: savior.blossom.base_stat,
                stat_per_level: savior.blossom.stat_per_level,
                skills: savior.blossom.skills,
            };
        }
        return savior;
    }, [savior, isBlossomed]);

    const [arcpointRevealed, setArcpointRevealed] = useState(false);

    useEffect(() => {
        setArcpointRevealed(false);
        dispatch(deactivateFullScreen());
    }, [savior, dispatch]);

    const openArcpoint = () => {
        if (!arcpointRevealed) {
            setArcpointRevealed(true);
            return;
        }
        dispatch(activateFullScreen());
    };

    const eng = savior ? savior.attr.replace("NUAT_", "").toLowerCase() : "";

    return (
        <div id="container" data-theme={theme} data-lang={lang}>
            {theme === "dark" && <StarContainer />}
            <Header title={getUIText(lang, "header.title.savior")} backTo={"/savior"} />

            {savior && (
                <div className={styles["savior-detail-container"]}>
                    <div className={styles["savior-summary"]}>
                        <div className={styles["savior-infos"]}>
                            <Portrait lang={lang} savior={savior} />
                            <div className={styles["savior-info"]}>
                                <div className={styles["name-row"]}>
                                    <h2>{savior.name[lang]}</h2>
                                    {canBlossom && (
                                        <button
                                            type="button"
                                            className={`${styles["blossom-toggle"]} ${isBlossomed ? styles["active"] : ""}`}
                                            onClick={toggleBlossom}
                                        >
                                            <span className={styles["blossom-icon"]}>🌸</span>
                                            <span className={styles["blossom-label"]}>{getUIText(lang, "savior.detail.blossom")}</span>
                                        </button>
                                    )}
                                </div>
                                <div className={styles["badges"]}>
                                    <span className={`${styles["badge"]} ${styles["rank"]} ${styles[displaySavior.rank]}`}>
                                        {displaySavior.rank}
                                    </span>
                                    <span className={`${styles["badge"]} ${styles[eng]}`}>{SAVIOR_ATTRIBUTE[savior.attr][lang]}</span>
                                    <span className={styles["badge"]}>{SAVIOR_CLASS[savior.class][lang]}</span>
                                    <span className={styles["badge"]}>{SAVIOR_ATK_TYPE[savior.atk_type][lang]}</span>
                                </div>
                            </div>
                        </div>
                        <p className={styles["savior-desc"]}>{savior.inst.desc[lang]}</p>
                    </div>

                    <StatusTab lang={lang} savior={displaySavior} isTabActive="active" />
                    <SkillTab lang={lang} savior={displaySavior} isTabActive="active" isBlossomed={isBlossomed} />
                    <ProfileDescTab lang={lang} savior={displaySavior} isTabActive="active" />

                    <div className={styles["illust-row"]}>
                        <div className={styles["illust-container"]}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/illust/${savior.name[LOCALES.KO]}(${savior.title[LOCALES.KO]}).webp`}
                                alt="Savior Illustration"
                            />
                        </div>
                        <div className={styles["arcpoint-container"]} onClick={openArcpoint}>
                            <h3>
                                {getUIText(lang, "savior.modal.arcpoint.title")}{" "}
                                <span className={styles["warning"]}>({getUIText(lang, "savior.modal.arcpoint.warning")})</span>
                            </h3>
                            <div className={`${styles["arcpoint-wrapper"]} ${arcpointRevealed ? styles["revealed"] : ""}`}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/arcpoint/${savior.name[LOCALES.KO]}(${savior.title[LOCALES.KO]}).webp`}
                                    alt="Arc Point"
                                />
                                <div className={styles["overlay"]}>
                                    <span>{getUIText(lang, "savior.modal.arcpoint.confirm")}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <FullScreenModal lang={lang} savior={savior} />

            <ScrollUpButton />
            <Footer />
        </div>
    );
}
